import React from "react";
import classnames from "classnames";
import { useSelector } from "react-redux";

import { DISPENSARY_PLACEHOLDER_PHOTO } from "constants/dispensary";
import { Dispensary, DispensaryTagsEnum } from "custom-types/Dispensary";
import useDomainCountryCode from "hooks/useDomainCountryCode";
import useWindowSize from "hooks/useWindowSize";
import {
  getLocalizedDistanceForCurrentDispensary,
  getReviewCount,
} from "redux/selectors/dispensary";
import { getCurrentMenuItem } from "redux/selectors/menuItem";
import { dispensaryType } from "utils/dispensaryUtils";
import { getStateOrProvinceLongName } from "utils/getStatesForCountry";

import BusinessHours from "components/Dispensary/DispensaryHeader/BusinessHours";
import ReviewRating from "components/Dispensary/DispensaryHeader/ReviewRating";
import DispensaryHeaderFulfillment from "components/Dispensary/DispensaryHeaderFulfillment";
import DispensaryPixelTracking from "components/DispensaryPixelTracking/DispensaryPixelTracking";
import StorefrontIcon from "components/Icons/storefront.svg";
import Image from "components/Image";

import FollowDispensaryButton from "../FollowDispensaryButton/FollowDispensaryButton";
import UnverifiedListing from "../UnverifiedListing";
import styles from "./DispensaryHeaderSlim.module.css";

const DispensaryHeaderSlim = ({
  reviewCount,
  dispensary,
  isInfoTier = false,
}: {
  reviewCount?: number;
  dispensary: Dispensary;
  hideRequestOnlineOrdering?: boolean;
  isInfoTier?: boolean;
}) => {
  const totalReviewCount = useSelector(getReviewCount);
  const localizedDistance = useSelector(
    getLocalizedDistanceForCurrentDispensary
  );
  const menuItem = useSelector(getCurrentMenuItem);
  const countryCode = useDomainCountryCode();

  const {
    logoUrl,
    name,
    inStoreCartEnabled,
    hasDeliveryEnabled,
    tags,
    pickupEnabled,
  } = dispensary;
  const { isMobile } = useWindowSize();

  const showDistance = !hasDeliveryEnabled || pickupEnabled;

  const dispensaryTypeString = dispensaryType({
    dispensaryTags: tags as DispensaryTagsEnum[],
    isCA: countryCode === "CA",
  });

  return (
    <>
      {dispensary.state && (
        <DispensaryPixelTracking dispensaryState={dispensary.state} />
      )}
      <div className={classnames("bg-white w-full", { "pb-sm": !menuItem })}>
        <div className="container">
          <div className="row lg:justify-between mt-6">
            <div className="col lg:col-5 flex flex-col gap-4 relative">
              <FollowDispensaryButton className="absolute right-0 dispensary-follow_button" />
              {isInfoTier ? (
                <div className="h-[50px] w-[50px] flex items-center justify-center bg-leafly-white rounded p-2">
                  <StorefrontIcon
                    className="opacity-20"
                    width="30"
                    height="30"
                  />
                </div>
              ) : (
                <div
                  className={classnames(
                    "flex-shrink-0 mr-sm",
                    styles["dispensary-header__logo"]
                  )}
                >
                  <Image
                    sizes={[50]}
                    alt={name}
                    className="rounded"
                    src={logoUrl || DISPENSARY_PLACEHOLDER_PHOTO}
                    data-testid="dispensary-header__logo"
                    disableLazyLoad
                  />
                </div>
              )}
              <div className="w-full">
                <div className="flex pt-md">
                  <div className="uppercase text-xs font-bold border border-light-grey rounded px-sm">
                    {dispensary.retailType}
                  </div>
                  {dispensaryTypeString && (
                    <div className="pl-xs">
                      <div className="uppercase text-xs font-bold border border-light-grey rounded px-sm">
                        {dispensaryTypeString}
                      </div>
                    </div>
                  )}
                </div>
                <h1 className="heading--m font-extrabold pr-xxl pb-xs pt-xs">
                  {name}
                </h1>
                <div className="flex mb-xs">
                  <div className="text-xs pt-[1.25px]">
                    {dispensary.city},&nbsp;
                    {getStateOrProvinceLongName(dispensary.state)}
                  </div>
                  <span
                    className={`mx-xs ${
                      totalReviewCount > 0 && "after:content-['•']"
                    }`}
                  />
                  <ReviewRating
                    totalReviews={reviewCount ?? totalReviewCount}
                    slug={dispensary.slug}
                  />
                </div>

                <div className="flex w-full justify-between mb-sm">
                  {showDistance && (
                    <div
                      className="text-xs lg:mr-section"
                      data-testid="dispensary-header__distance"
                    >
                      {localizedDistance.full}
                    </div>
                  )}
                  <BusinessHours
                    className="lg:ml-section"
                    dispensary={dispensary}
                  />
                </div>
                {isMobile && isInfoTier && (
                  <UnverifiedListing dispensaryId={dispensary.id} />
                )}
              </div>
            </div>

            <div className="col lg:col-5">
              <DispensaryHeaderFulfillment
                hideRequestOnlineOrdering={inStoreCartEnabled || isInfoTier}
              />
              {!isMobile && isInfoTier && (
                <UnverifiedListing dispensaryId={dispensary.id} />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DispensaryHeaderSlim;
